import React, { useState } from "react";
import { motion } from "framer-motion";
import type { PetState } from "@miezmission/shared-types";
import { PetMood } from "@miezmission/shared-types";
import { PetCard } from "@miezmission/ui";

// ─── Types ────────────────────────────────────────────────────────────────────

interface TodayTask {
  id: string;
  emoji: string;
  title: string;
  durationMinutes: number;
  completed: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MOOD_LABEL: Record<PetMood, string> = {
  [PetMood.Ecstatic]: "überglücklich",
  [PetMood.Happy]: "fröhlich",
  [PetMood.Content]: "zufrieden",
  [PetMood.Neutral]: "entspannt",
  [PetMood.Sad]: "traurig",
  [PetMood.Unhappy]: "unglücklich",
  [PetMood.Distressed]: "verzweifelt",
};

const MOOD_EMOJI: Record<PetMood, string> = {
  [PetMood.Ecstatic]: "😸",
  [PetMood.Happy]: "😺",
  [PetMood.Content]: "🐱",
  [PetMood.Neutral]: "😐",
  [PetMood.Sad]: "😿",
  [PetMood.Unhappy]: "🙀",
  [PetMood.Distressed]: "😾",
};

// Placeholder pet — replace with API data
const PLACEHOLDER_PET: PetState = {
  id: "pet-1",
  familyId: "family-1",
  name: "Miezi",
  happiness: 72,
  streakDays: 4,
  lastInteractionAt: new Date(),
  mood: PetMood.Happy,
  level: 3,
  experiencePoints: 1250,
  createdAt: new Date(),
  updatedAt: new Date(),
};

const PLACEHOLDER_TASKS: TodayTask[] = [
  { id: "1", emoji: "🦷", title: "Zähne putzen", durationMinutes: 3, completed: false },
  { id: "2", emoji: "👕", title: "Anziehen", durationMinutes: 5, completed: false },
  { id: "3", emoji: "🎒", title: "Schulranzen packen", durationMinutes: 5, completed: false },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function ChildHome(): React.ReactElement {
  const [pet] = useState<PetState>(PLACEHOLDER_PET);
  const [tasks, setTasks] = useState<TodayTask[]>(PLACEHOLDER_TASKS);

  const completedCount = tasks.filter((t) => t.completed).length;

  function toggleTask(id: string): void {
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, completed: !t.completed } : t)),
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4 pb-8">
      {/* Pet card */}
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <PetCard petState={pet} />
      </motion.div>

      {/* Mood banner */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.15 }}
        className="flex items-center gap-3 rounded-2xl bg-purple-50 px-4 py-3"
      >
        <span className="text-3xl">{MOOD_EMOJI[pet.mood]}</span>
        <div>
          <p className="text-sm font-semibold text-purple-800">
            {pet.name} ist gerade {MOOD_LABEL[pet.mood]}!
          </p>
          <p className="text-xs text-purple-600">
            🔥 {pet.streakDays} {pet.streakDays === 1 ? "Tag" : "Tage"} in Folge – weiter so!
          </p>
        </div>
      </motion.div>

      {/* Today's task list */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-800">Heute</h2>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            {completedCount}/{tasks.length} erledigt
          </span>
        </div>

        <div className="flex flex-col gap-2">
          {tasks.map((task, index) => (
            <motion.button
              key={task.id}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * index }}
              whileTap={{ scale: 0.97 }}
              onClick={() => { toggleTask(task.id); }}
              className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left shadow-sm transition-colors ${
                task.completed
                  ? "bg-green-50"
                  : "bg-white active:bg-gray-50"
              }`}
            >
              <span className="text-2xl">{task.emoji}</span>
              <div className="flex-1 min-w-0">
                <p
                  className={`truncate text-sm font-medium ${
                    task.completed ? "text-gray-400 line-through" : "text-gray-800"
                  }`}
                >
                  {task.title}
                </p>
                <p className="text-xs text-gray-400">{task.durationMinutes} Min.</p>
              </div>
              <motion.span
                animate={{ scale: task.completed ? [1, 1.3, 1] : 1 }}
                className="shrink-0 text-xl"
              >
                {task.completed ? "✅" : "⭕"}
              </motion.span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* All-done celebration */}
      {completedCount === tasks.length && tasks.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 p-4 text-center text-white shadow-md"
        >
          <p className="text-xl font-bold">🎉 Alles erledigt!</p>
          <p className="mt-1 text-sm opacity-90">
            {pet.name} ist so stolz auf dich!
          </p>
        </motion.div>
      )}
    </div>
  );
}
